import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Publication,
  PublicationsCollection,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { maxLength, required } from '@freelancer/ui/validators';
import { isFormControl } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

enum PortfolioFormInput {
  TITLE = 'title',
  PUBLISHER = 'publisher',
  SUMMARY = 'summary',
}

@Component({
  selector: 'app-user-profile-publications-edit',
  template: `
    <fl-grid>
      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Publication title label" [weight]="FontWeight.BOLD">
          Publication Title
        </fl-text>
        <ng-container
          *ngIf="portfolioForm.get(PortfolioFormInput.TITLE) as control"
        >
          <fl-input
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfilePublicationEditTitle"
            i18n-placeholder="Profile publication title placeholder"
            placeholder="Enter the publication title"
            [control]="control"
          ></fl-input>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Publication publisher label" [weight]="FontWeight.BOLD">
          Publisher
        </fl-text>
        <ng-container
          *ngIf="portfolioForm.get(PortfolioFormInput.PUBLISHER) as control"
        >
          <fl-input
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfilePublicationEditPublisher"
            i18n-placeholder="Profile publication publisher placeholder"
            placeholder="Enter the publisher"
            [control]="control"
          ></fl-input>
        </ng-container>
      </fl-col>

      <fl-col
        [col]="12"
        [flMarginBottom]="
          (hideActionRow$ | flAsync) ? Margin.XXXSMALL : Margin.MID
        "
      >
        <fl-text i18n="Publication summary label" [weight]="FontWeight.BOLD">
          Summary
        </fl-text>
        <ng-container
          *ngIf="portfolioForm.get(PortfolioFormInput.SUMMARY) as control"
        >
          <fl-textarea
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfilePublicationEditSummary"
            i18n-placeholder="Profile publication description placeholder"
            placeholder="Enter a description of the publication"
            [control]="control"
            [rows]="5"
          ></fl-textarea>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-banner-alert
          *ngIf="isError$ | flAsync"
          i18n="Error message label"
          [type]="BannerAlertType.ERROR"
        >
          Oops, something went wrong when saving your publication item. Please
          try again.
        </fl-banner-alert>
      </fl-col>

      <fl-col
        *ngIf="needsDeleteConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to delete this publication?"
          flTrackingLabel="UserProfilePublicationEditDelete"
          i18n-body="Delete publication confirmation body"
          i18n-title="Delete publication confirmation title"
          title="Delete Publication"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDeletePublication()"
          (confirm)="handleConfirmDeletePublication()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col
        *ngIf="needsCancelConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to discard any changes?"
          flTrackingLabel="UserProfilePublicationEditDiscardChanges"
          i18n-body="Discard changes confirmation body"
          i18n-title="Discard changes confirmation title"
          title="Discard Changes"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDiscardChanges()"
          (confirm)="handleConfirmDiscardChanges()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col *ngIf="!(hideActionRow$ | flAsync)" [col]="12">
        <app-user-profile-editable-ui-action-row
          deleteLinkText="Delete Publication"
          flTrackingLabel="UserProfilePublicationsEdit"
          i18n-deleteLinkText="Delete Publication link label"
          [disabled]="!portfolioForm.valid || !portfolioForm.dirty"
          [isBusy]="isBusy$ | flAsync"
          [showDeleteButton]="!!(currentPublicationId$ | flAsync)"
          (cancel)="handleCancel()"
          (delete)="handleDeletePublication()"
          (save)="handleSave()"
        >
        </app-user-profile-editable-ui-action-row>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./user-profile-publications-edit.component.scss'],
})
export class UserProfilePublicationsEditComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  isFormControl = isFormControl;
  Margin = Margin;
  PortfolioFormInput = PortfolioFormInput;

  @Input() publication$: Rx.Observable<Publication | undefined>;
  @Input() publicationsCollection: DatastoreCollection<PublicationsCollection>;
  @Output() cancel = new EventEmitter<void>();

  private _isBusy$ = new Rx.BehaviorSubject<boolean>(false);
  isBusy$ = this._isBusy$.asObservable();

  private _isError$ = new Rx.BehaviorSubject<boolean>(false);
  isError$ = this._isError$.asObservable();

  private _needsDeleteConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsDeleteConfirmation$ = this._needsDeleteConfirmation$.asObservable();

  private _needsCancelConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsCancelConfirmation$ = this._needsCancelConfirmation$.asObservable();

  // If this is set the user is currently editing a publication and if it is
  // undefined the user is creating a new publication item.
  private _currentPublicationId$ = new Rx.BehaviorSubject<number | undefined>(
    undefined,
  );
  currentPublicationId$ = this._currentPublicationId$.asObservable();

  hideActionRow$: Rx.Observable<boolean>;
  publicationSubscription?: Rx.Subscription;

  publisherControl = new FormControl('', [
    required($localize`Please enter a publisher.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);
  summaryControl = new FormControl('', [
    required($localize`Please enter a publication summary.`),
    maxLength(500, $localize`Please enter at most 500 characters.`),
  ]);
  titleControl = new FormControl('', [
    required($localize`Please enter a publication title.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);

  portfolioForm = new FormGroup({
    [PortfolioFormInput.PUBLISHER]: this.publisherControl,
    [PortfolioFormInput.SUMMARY]: this.summaryControl,
    [PortfolioFormInput.TITLE]: this.titleControl,
  });

  ngOnInit() {
    this.hideActionRow$ = Rx.combineLatest([
      this._needsDeleteConfirmation$,
      this._needsCancelConfirmation$,
    ]).pipe(
      map(([deleteConfirm, cancelConfirm]) => deleteConfirm || cancelConfirm),
    );

    this.publicationSubscription = this.publication$.subscribe(publication => {
      // If the publication is undefined then we are adding a new publication.
      if (!publication) {
        this._currentPublicationId$.next(undefined);
        return;
      }

      this._currentPublicationId$.next(publication.id);
      this.publisherControl.setValue(publication.publisher);
      this.summaryControl.setValue(publication.summary);
      this.titleControl.setValue(publication.title);
    });
  }

  handleSave(): void {
    const publicationId = this._currentPublicationId$.getValue();
    // If we have a publication id the publication already exists so we need to
    // update it instead of create a new one.
    if (publicationId) {
      this.updatePublication(publicationId);
    } else {
      this.createNewPublication();
    }
  }

  createNewPublication(): void {
    this._isBusy$.next(true);
    this.publicationsCollection
      .push({
        userId: -1, // Needs the userId set. Not actually used in request.
        id: Date.now(),
        title: this.titleControl.value,
        publisher: this.publisherControl.value,
        summary: this.summaryControl.value,
      })
      .then(response => {
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
          return;
        }

        // Cancel out of the edit view on success.
        this.cancel.emit();
      });
  }

  updatePublication(publicationId: number): void {
    this._isBusy$.next(true);

    this.publicationsCollection
      .update(publicationId, {
        title: this.titleControl.value,
        publisher: this.publisherControl.value,
        summary: this.summaryControl.value,
      })
      .then(response => {
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
          return;
        }

        // Cancel out of the edit view on success.
        this.cancel.emit();
      });
  }

  handleConfirmDeletePublication(): void {
    this._isBusy$.next(true);
    const publicationId = this._currentPublicationId$.getValue();
    // If this is not defined we should not be able to delete the publication.
    if (!publicationId) {
      return;
    }

    this.publicationsCollection.remove(publicationId).then(response => {
      this._needsDeleteConfirmation$.next(false);
      this._isBusy$.next(false);

      if (response.status === 'error') {
        this._isError$.next(true);
        return;
      }

      // Cancel out of the edit view on success.
      this.cancel.emit();
    });
  }

  handleCancelDeletePublication(): void {
    this._needsDeleteConfirmation$.next(false);
  }

  handleCancelDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
  }

  handleConfirmDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
    this.cancel.emit();
  }

  handleCancel(): void {
    if (this.portfolioForm.dirty) {
      this._needsCancelConfirmation$.next(true);
      return;
    }
    this.cancel.emit();
  }

  handleDeletePublication(): void {
    this._needsDeleteConfirmation$.next(true);
  }

  ngOnDestroy(): void {
    if (this.publicationSubscription) {
      this.publicationSubscription.unsubscribe();
    }
  }
}
